/**
 * PreparatoryCaregiverObservation.jsx
 * -----------------------------------
 * A self-contained form / viewer for the caregiver-observation assignment
 * (Preparatory stage, Grades 3-5).  Uses Tailwind CSS + Lucide icons.
 *
 * Props
 * ──► childId            (Mongo ID string)
 * ──► assignmentId?      (string)  – optional; if omitted but the caller
 *                        passes `submission`, we fall back to
 *                        submission.assignment_id.
 * ──► submission?        (object)  – if present, the component renders a
 *                        read-only card instead of the editable form.
 * ──► onSubmitComplete?  (fn)      – callback fired after a successful POST.
 *
 * External deps
 * ──► ../api/preparatoryApiService   (default-exported service shown in your repo)
 * ──► lucide-react                   (for Save + AlertCircle icons)
 */

import React, {useEffect, useState} from "react";
import {AlertCircle, CheckCircle, Save} from "lucide-react";
import preparatoryApiService from "../api/preparatoryApiService";

// --- Enum-like helpers straight from the service ------------------------------
const {HomeResource, ParentResponse, SupportArea} = preparatoryApiService;

// Home-resource tiles (label + emoji for nice visuals)
const RESOURCE_OPTIONS = [
    {id: HomeResource.BOOKS_MAGAZINES, label: "Books and Magazines", emoji: "📚"},
    {id: HomeResource.NEWSPAPERS, label: "Newspapers", emoji: "📰"},
    {id: HomeResource.TOYS_GAMES_SPORTS, label: "Toys, Games and sports", emoji: "🧸"},
    {id: HomeResource.PHONE_COMPUTER, label: "Phone and Computer", emoji: "💻"},
    {id: HomeResource.INTERNET, label: "Internet", emoji: "🌐"},
    {id: HomeResource.PUBLIC_BROADCAST, label: "Public Broadcast System", emoji: "📻"},
    {id: HomeResource.CWSN_RESOURCES, label: "Resources for CWSN", emoji: "♿"},
];

// Assessment statements (order matters – matches your PDF)
const ASSESSMENT_QUESTIONS = [
    {id: "finds_school_safe", text: "My child finds the classroom and school a welcoming and safe space."},
    {id: "participates_in_activities", text: "My child participates in academic and other activities in school."},
    {id: "finds_curriculum_difficult", text: "My child finds the grade-level curriculum difficult.", reverse: true},
    {id: "making_good_progress", text: "My child is making good progress as per their grade."},
    {id: "gets_school_support", text: "My child is getting the support needed from school."},
    {id: "can_talk_feelings", text: "My child can talk about how he/she feels, e.g., happy, upset, or angry."},
    {id: "can_calm_down", text: "My child can calm himself/herself down during difficult situations."},
    {id: "can_understand_friends", text: "My child can understand how his/her friends feel."},
    {id: "respects_opinions", text: "My child respects everyone's opinions."},
    {id: "helps_after_fight", text: "My child can help his/her friends make up after a fight."},
    {id: "comforts_when_sad", text: "When someone is sad, my child can make them feel better."},
];

// Support-needs check-list
const SUPPORT_OPTIONS = [
    {id: SupportArea.ORAL_COMMUNICATION, label: "Oral communication (R1 or R2)"},
    {id: SupportArea.READING, label: "Reading"},
    {id: SupportArea.NUMBERS_AND_MATH, label: "Numbers and Math"},
    {id: SupportArea.SELF_CONFIDENCE, label: "Self-confidence"},
    {id: SupportArea.WORKING_WITH_CHILDREN, label: "Working with other children"},
    {id: SupportArea.WORKING_INDEPENDENTLY, label: "Working independently at home"},
];

// Fixed display order for the 4 response buttons
const RESPONSE_ORDER = [
    ParentResponse.YES,
    ParentResponse.SOMETIMES,
    ParentResponse.NO,
    ParentResponse.NOT_SURE,
];

// Turn a response value into its (possibly reversed) emoji pair
const emojiFor = (resp, reverse = false) => {
    const base = {
        [ParentResponse.YES]: "😊👍",
        [ParentResponse.SOMETIMES]: "😐👉",
        [ParentResponse.NO]: "😔👎",
        [ParentResponse.NOT_SURE]: "❓",
    };
    if (!reverse) return base[resp];
    // Swap YES/NO when statement is negatively worded
    return resp === ParentResponse.YES
        ? base[ParentResponse.NO]
        : resp === ParentResponse.NO
            ? base[ParentResponse.YES]
            : base[resp];
};

/* ───────────────────────────  MAIN COMPONENT  ─────────────────────────── */

export default function PreparatoryCaregiverObservation({
                                                            childId,
                                                            assignmentId: propAssignmentId,
                                                            submission,
                                                            onSubmitComplete,
                                                        }) {
    // Prefer explicit prop; fall back to submission object if present
    const assignmentId =
        propAssignmentId ?? submission?.assignment_id ?? null;

    /* ------------------------- local form state ------------------------ */
    const [form, setForm] = useState({
        resources: [],
        other_resources: "",
        assessment: ASSESSMENT_QUESTIONS.reduce(
            (acc, q) => ({...acc, [q.id]: ParentResponse.NOT_SURE}),
            {}
        ),
        needs_support: [],
        other_support: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);

    // Load existing submission data if available
    useEffect(() => {
        if (submission?.caregiver_observation) {
            const obs = submission.caregiver_observation;
            setForm({
                resources: obs.resources || [],
                other_resources: obs.other_resources || "",
                assessment: obs.assessment || ASSESSMENT_QUESTIONS.reduce(
                    (acc, q) => ({...acc, [q.id]: ParentResponse.NOT_SURE}),
                    {}
                ),
                needs_support: obs.needs_support || [],
                other_support: obs.other_support || "",
            });
        }
    }, [submission]);

    /* ----------------------- little state helpers ---------------------- */
    const toggle = (arr, v) =>
        arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

    const handleResource = (id) =>
        setForm((f) => ({...f, resources: toggle(f.resources, id)}));

    const handleSupport = (id) =>
        setForm((f) => ({...f, needs_support: toggle(f.needs_support, id)}));

    const setAssessment = (id, resp) =>
        setForm((f) => ({
            ...f,
            assessment: {...f.assessment, [id]: resp},
        }));

    const handleChange = (e) =>
        setForm((f) => ({...f, [e.target.name]: e.target.value}));

    /* ----------------------------- submit ------------------------------ */
    const submit = async (e) => {
        e.preventDefault();

        // Validation
        if (!childId) {
            setError("Child ID is required.");
            return;
        }

        if (!assignmentId) {
            setError("Assignment ID is required to submit feedback.");
            return;
        }

        // Check if at least one assessment question is answered (not NOT_SURE)
        const hasAnswers = Object.values(form.assessment).some(
            response => response !== ParentResponse.NOT_SURE
        );

        if (!hasAnswers) {
            setError("Please answer at least one assessment question.");
            return;
        }

        setIsSubmitting(true);
        setError(null);
        setSuccess(false);

        try {
            console.log("Submitting caregiver observation:", {
                childId,
                assignmentId,
                formData: form
            });

            const result = await preparatoryApiService.addCaregiverObservation(
                childId,
                assignmentId,
                form
            );

            console.log("Submission successful:", result);
            setSuccess(true);

            // Call parent callback after successful submission
            if (onSubmitComplete) {
                onSubmitComplete(result);
            }

            // Clear form after successful submission
            setForm({
                resources: [],
                other_resources: "",
                assessment: ASSESSMENT_QUESTIONS.reduce(
                    (acc, q) => ({...acc, [q.id]: ParentResponse.NOT_SURE}),
                    {}
                ),
                needs_support: [],
                other_support: "",
            });

        } catch (err) {
            console.error("Submission error:", err);
            setError(err.message || "Failed to submit caregiver observation. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    /* ----------------- reusable row for each statement ---------------- */
    const AssessmentRow = ({q, viewOnly}) => {
        // Add proper null checks and fallbacks to fix the error
        const assessmentData = submission?.caregiver_observation?.assessment || {};

        const selected = viewOnly
            ? assessmentData[q.id] || ParentResponse.NOT_SURE
            : form.assessment[q.id];

        return (
            <>
                {/* Emoji buttons row */}
                <tr className="border-b border-orange-200 bg-orange-50">
                    <td
                        rowSpan={2}
                        className="w-2/5 px-4 py-3 text-sm font-medium align-top"
                    >
                        {q.text}
                    </td>

                    {RESPONSE_ORDER.map((resp) => (
                        <td key={resp} className="px-3 py-3 text-center">
                            {viewOnly ? (
                                <span
                                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xl ${
                                        selected === resp ? "ring-2 ring-orange-600" : ""
                                    }`}
                                >
                                    {emojiFor(resp, q.reverse)}
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => setAssessment(q.id, resp)}
                                    className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-xl transition hover:bg-orange-100 ${
                                        selected === resp
                                            ? "bg-orange-200 ring-2 ring-orange-600"
                                            : "bg-white"
                                    }`}
                                >
                                    {emojiFor(resp, q.reverse)}
                                </button>
                            )}
                        </td>
                    ))}
                </tr>

                {/* YES / SOMETIMES / NO / NOT SURE label row */}
                <tr className="bg-orange-500 text-white text-xs uppercase tracking-wide">
                    {RESPONSE_ORDER.map((resp) => (
                        <td key={resp} className="py-1 text-center font-semibold">
                            {resp}
                        </td>
                    ))}
                </tr>
            </>
        );
    };

    /* ========================== READ-ONLY CARD ========================= */
    if (submission?.caregiver_observation) {
        const obs = submission.caregiver_observation;

        return (
            <section className="font-[poppins] bg-white border border-gray-200 shadow rounded-lg overflow-hidden">
                <header className="p-6 text-center">
                    <h2 className="text-2xl font-bold text-orange-600">
                        Your Child Matters!
                    </h2>
                    <p className="text-gray-600 mt-1">
                        Observation submitted on{" "}
                        {new Date(obs.created_at || new Date()).toLocaleDateString()}
                    </p>
                </header>

                {/* ➜ Resources */}
                <div className="border-t border-gray-200 px-6 py-4">
                    <h3 className="text-lg font-semibold mb-4">
                        Resources available to your child at home
                    </h3>

                    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-4">
                        {RESOURCE_OPTIONS.map((r) => (
                            <div
                                key={r.id}
                                className={`flex flex-col items-center p-2 rounded-lg ${
                                    (obs.resources ?? []).includes(r.id) ? "bg-green-50" : "bg-gray-50"
                                }`}
                            >
                                <div
                                    className={`h-16 w-16 flex items-center justify-center rounded-lg text-4xl mb-2 ${
                                        (obs.resources ?? []).includes(r.id)
                                            ? "border-2 border-green-500"
                                            : "border"
                                    }`}
                                >
                                    {r.emoji}
                                </div>
                                <span className="text-xs text-center font-medium">
                                    {r.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {obs.other_resources && (
                        <p className="mt-3 text-sm bg-gray-50 p-2 rounded">
                            <span className="font-semibold">Other: </span>
                            {obs.other_resources}
                        </p>
                    )}
                </div>

                {/* ➜ Assessment */}
                <div className="mt-6">
                    <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                        How can I know your child better?
                    </div>
                    <table className="min-w-full border border-orange-200">
                        <tbody>
                        {ASSESSMENT_QUESTIONS.map((q) => (
                            <AssessmentRow key={q.id} q={q} viewOnly/>
                        ))}
                        </tbody>
                    </table>
                </div>

                {/* ➜ Support needs */}
                <div className="mt-6">
                    <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                        My child needs support with...
                    </div>
                    <div className="border border-t-0 border-orange-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {SUPPORT_OPTIONS.map((s) => (
                            <div key={s.id} className="flex items-center">
                                {(obs.needs_support ?? []).includes(s.id) && (
                                    <svg
                                        className="w-4 h-4 mr-2 text-green-600"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                )}
                                <span
                                    className={
                                        (obs.needs_support ?? []).includes(s.id)
                                            ? "font-medium"
                                            : "text-gray-500"
                                    }
                                >
                                    {s.label}
                                </span>
                            </div>
                        ))}
                    </div>

                    {obs.other_support && (
                        <p className="mt-3 text-sm bg-gray-50 p-2 rounded">
                            <span className="font-semibold">Other: </span>
                            {obs.other_support}
                        </p>
                    )}
                </div>
            </section>
        );
    }

    /* ============================ EDIT FORM ============================ */
    return (
        <form
            onSubmit={submit}
            className="font-[poppins] bg-white border border-gray-200 shadow rounded-lg overflow-hidden"
        >
            <header className="p-6 text-center">
                <h2 className="text-2xl font-bold text-orange-600">
                    Your Child Matters!
                </h2>
                <p className="text-gray-600 mt-1">
                    Help us understand how we can better support your child.
                </p>
            </header>

            {/* Success message */}
            {success && (
                <div className="mx-6 mb-4 flex items-center bg-green-100 text-green-800 p-3 rounded">
                    <CheckCircle className="w-5 h-5 mr-2"/>
                    Feedback submitted successfully! Thank you for your input.
                </div>
            )}

            {/* ➜ Home resources (icons + checkboxes) */}
            <section className="border-t border-gray-200 px-6 py-4">
                <h3 className="text-lg font-semibold mb-4">
                    Tick the resources available to your child at home.
                </h3>

                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-5">
                    {RESOURCE_OPTIONS.map((r) => (
                        <div key={r.id} className="flex flex-col items-center">
                            <button
                                type="button"
                                onClick={() => handleResource(r.id)}
                                className={`h-16 w-16 flex items-center justify-center rounded-lg text-4xl mb-2 transition ${
                                    form.resources.includes(r.id)
                                        ? "bg-orange-200 border-2 border-orange-500"
                                        : "bg-gray-100 hover:bg-orange-50"
                                }`}
                            >
                                {r.emoji}
                            </button>
                            <span className="text-sm text-center font-medium mb-1">
                                {r.label}
                            </span>
                            <input
                                type="checkbox"
                                checked={form.resources.includes(r.id)}
                                onChange={() => handleResource(r.id)}
                                className="h-4 w-4 text-orange-600 border-gray-300 rounded"
                            />
                        </div>
                    ))}
                </div>

                <div className="mt-4">
                    <label
                        htmlFor="other_resources"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Any other (please specify)
                    </label>
                    <input
                        id="other_resources"
                        name="other_resources"
                        value={form.other_resources}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Specify other resources available at home"
                    />
                </div>
            </section>

            {/* ➜ Assessment table */}
            <section className="mt-6">
                <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                    How can I know your child better?
                    <p className="text-orange-100 text-sm font-normal">
                        Circle the most appropriate option for each statement.
                    </p>
                </div>

                <table className="min-w-full border border-orange-200">
                    <tbody>
                    {ASSESSMENT_QUESTIONS.map((q) => (
                        <AssessmentRow key={q.id} q={q} viewOnly={false}/>
                    ))}
                    </tbody>
                </table>
            </section>

            {/* ➜ Support needs */}
            <section className="mt-6">
                <div className="bg-orange-500 text-white py-3 px-4 font-semibold">
                    My child needs support with...
                </div>

                <div className="border border-t-0 border-orange-200 p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {SUPPORT_OPTIONS.map((s) => (
                        <label key={s.id} className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                className="h-4 w-4 text-orange-600 border-gray-300 rounded"
                                checked={form.needs_support.includes(s.id)}
                                onChange={() => handleSupport(s.id)}
                            />
                            <span className="text-sm">{s.label}</span>
                        </label>
                    ))}
                </div>

                <div className="border border-t-0 border-orange-200 p-4">
                    <label
                        htmlFor="other_support"
                        className="block text-sm font-medium text-gray-700 mb-1"
                    >
                        Other subject areas (Specify)
                    </label>
                    <input
                        id="other_support"
                        name="other_support"
                        value={form.other_support}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-orange-500 focus:border-orange-500"
                        placeholder="Specify other areas where support is needed"
                    />
                </div>
            </section>

            {/* ➜ Submit / error bar */}
            <div className="border-t border-gray-200 px-6 py-4">
                {error && (
                    <div className="flex items-center bg-red-100 text-red-800 p-3 rounded mb-4">
                        <AlertCircle className="w-5 h-5 mr-2"/>
                        {error}
                    </div>
                )}

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="inline-flex items-center px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded shadow font-medium transition-colors"
                    >
                        {isSubmitting ? (
                            <svg
                                className="animate-spin h-5 w-5 mr-3"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                        ) : (
                            <Save size={20} className="mr-2"/>
                        )}
                        {isSubmitting ? "Saving..." : "Submit Feedback"}
                    </button>
                </div>
            </div>
        </form>
    );
}